import apiClient from 'apiClient';
import User from 'auth/User';
import { logout as logoutAction } from 'auth/store/actions';
import { getIsLoggedIn } from 'auth/store/selectors';
import cx from 'classnames';
import Logo from 'components/Logo/Logo';
import { useRebrandContext } from 'helpers/hooks/useRebrandContext';
import { Icon, Nav } from 'lmc-ui-library';
import { Button, Icon as NewUiIcon, Nav as NewUiNav } from 'lmc-ui-library-rebrand';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { PAGES_ENUM, PERMISSION_ENUM } from 'routes/enums';
import permissionService from 'routes/permissionService';
import { login, profile, registerSearchFirms } from 'routes/routePaths';
import FullScreenModal from 'shared/InfoModal/FullScreenModal';
import { getCookie, setCookie } from 'utils/cookies';
import homePage, { navigateExternal } from 'utils/navigation/navigation';
import { Nullable } from 'utils/types';
import css from './NavBar.module.scss';

type Props = {
  fixed?: boolean;
  newUi?: boolean;
};

const currentAppVersion = process.env.REACT_APP_CURRENT_APP_SHA;

const contactData = (newUi: boolean = false) => ({
  title: 'Contact Us',
  content: [
    <>
      <p className="bold">Underwriting</p>
      <a href="mailto:caseupdate@livemoremortgages.com" className={`margin-bottom ${newUi ? 'email' : 'plum'}`}>
        caseupdate@livemoremortgages.com
      </a>
    </>,
    <>
      <p className="bold">Sales</p>
      <a href="mailto:sales@livemoremortgages.com" className={`${newUi ? 'email' : 'plum'}`}>
        sales@livemoremortgages.com
      </a>
    </>,
  ],
});

const NavBar: React.FC<Props> = ({ fixed = false }) => {
  const [shouldShowModal, setShouldShowModal] = useState<boolean>(false);
  const [newUi] = useRebrandContext();

  const dispatch = useDispatch();
  const history = useHistory();
  const isLoggedIn = useSelector((state: any) => getIsLoggedIn(state));
  const adminUrl: string = process.env.REACT_APP_DJANGO_ADMIN_URL || '';
  const adminLoginUrl = `${adminUrl}/login/`;
  const user: User = useSelector((state: any) => state.auth.user);
  const accessToken: Nullable<string> = useSelector((state: any) => state.auth.accessToken);
  const hasAdminAccess =
    permissionService.hasPermission(user, PAGES_ENUM.ADMIN_PANEL, PERMISSION_ENUM.ADMINISTRATIVE) && adminUrl;
  const isPanel = permissionService.hasLMCPermissions(user);

  const logout = useCallback(async () => {
    await dispatch(logoutAction());
    history.push(login);
  }, [dispatch, history]);

  const handleOnClick = () => setShouldShowModal(true);

  const handleCloseModal = () => setShouldShowModal(false);

  const redirectToAdminPanel = async () => {
    await apiClient.get(adminLoginUrl, { headers: { Authorization: `Bearer ${accessToken}` } });
    window.location.href = adminUrl;
  };

  const redirectToHome = async () => {
    const homePageRoute = homePage(user);
    homePageRoute.isExternal ? navigateExternal(homePageRoute.route) : history.push(homePageRoute.route);
  };

  const handleAdminPanelClick = (e: any) => {
    e.preventDefault();
    redirectToAdminPanel();
  };

  useEffect(() => {
    if (newUi) {
      document.body.style.backgroundColor = 'white';
      return;
    }
    document.body.style.backgroundColor = '';
  }, [newUi]);

  useEffect(() => {
    const appVersionSha = getCookie('app_version_sha');
    if (currentAppVersion) {
      if (!appVersionSha || appVersionSha !== currentAppVersion) {
        setCookie('app_version_sha', currentAppVersion);
        if (isLoggedIn) {
          logout();
        }
      }
    }
  }, [isLoggedIn, logout]);

  const NavComponent = newUi ? NewUiNav : Nav;
  const IconComponent = newUi ? NewUiIcon : Icon;
  const iconColor = `var(--color-${newUi ? 'extra-white' : 'putty'})`;

  return (
    <NavComponent
      variant={isPanel ? 'dark' : 'website'}
      className={cx(css.nav, { [css.fixed]: fixed }, newUi && css.newUi)}>
      <Nav.Container>
        <div onClick={redirectToHome} className={css.logoLink}>
          <Logo newUi={newUi} />
        </div>

        {!isLoggedIn && (
          <div className={css.hideSm}>
            <Nav.Links>
              <Nav.Link>
                <Link to={login} className={`${css.link} ${css.signInLink}`}>
                  <NewUiIcon type="account" color={iconColor} />
                  Sign In
                </Link>
              </Nav.Link>
              <Nav.Link>
                <Link to={registerSearchFirms} className={`${css.link} ${css.registerLink}`}>
                  <Button
                    mode="ghost"
                    borderVariant="rectangle"
                    children={'Register'}
                    className={css.registerBtn}
                    small
                  />
                </Link>
              </Nav.Link>
            </Nav.Links>
          </div>
        )}
        {isLoggedIn && (
          <Nav.Links>
            {hasAdminAccess && (
              <Nav.Link>
                <a
                  href={adminUrl}
                  className={css.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleAdminPanelClick}>
                  Django Admin
                </a>
              </Nav.Link>
            )}
            <Nav.Link>
              <button className={css.link} onClick={handleOnClick}>
                {newUi && <NewUiIcon type="phoneCall" color={iconColor} />}
                Contact
              </button>
            </Nav.Link>

            <Nav.Link>
              <button className={css.link} onClick={redirectToHome}>
                <IconComponent type="home" color={iconColor} />
                Home
              </button>
            </Nav.Link>

            {permissionService.hasAnyPermission(user, PAGES_ENUM.PROFILE) && (
              <Nav.Link>
                <Link to={profile} className={css.link}>
                  <IconComponent type="account" color={iconColor} />
                  Profile
                </Link>
              </Nav.Link>
            )}

            <Nav.Link>
              <button className={css.link} onClick={logout}>
                <IconComponent type="exit" color={iconColor} />
                Log out
              </button>
            </Nav.Link>
          </Nav.Links>
        )}
      </Nav.Container>
      {shouldShowModal && (
        <FullScreenModal
          title={contactData(newUi).title}
          infos={contactData(newUi).content}
          onClose={handleCloseModal}
          wrapperClass={newUi ? 'newUi' : ''}
        />
      )}
    </NavComponent>
  );
};

export default NavBar;
